import { useLayoutEffect } from "react";
import { useGamificationStore } from "@/stores/useGamificationStore";
import { useThemeStore } from "@/stores/useThemeStore";

const THEME_STORAGE_KEY = "ld_theme";
const SHOP_THEMES = ["matrixTheme", "neonTheme", "sakuraTheme", "cyberpunkTheme"] as const;

const THEME_TO_ATTR: Record<string, string> = {
  matrixTheme: "matrix",
  neonTheme: "neon",
  sakuraTheme: "sakura",
  cyberpunkTheme: "cyberpunk",
  darkMode: "matrix",
};

/** Light themes that must NOT force .dark class */
const LIGHT_THEMES = ["sakuraTheme"];

function getStandardThemePreference(): "dark" | "light" | "system" {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (v === "dark" || v === "light" || v === "system") return v;
  } catch (_) {}
  return "dark";
}

function wantsDarkMode(preference: "dark" | "light" | "system"): boolean {
  if (preference === "dark") return true;
  if (preference === "light") return false;
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * Theme sync: premium theme OR standard Light/Dark from useThemeStore / ld_theme.
 * When equippedTheme is null, CRITICALLY re-apply .dark based on user preference.
 */
export default function ThemeApplicator() {
  const equippedTheme = useGamificationStore((s) => s.equippedTheme);
  const systemTheme = useThemeStore((s) => s.systemTheme);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    if (equippedTheme != null && (SHOP_THEMES.includes(equippedTheme as (typeof SHOP_THEMES)[number]) || equippedTheme === "darkMode")) {
      // Premium theme active: data-theme
      const attrValue = THEME_TO_ATTR[equippedTheme] ?? equippedTheme;
      root.setAttribute("data-theme", attrValue);
      root.setAttribute("data-equipped-theme", equippedTheme === "darkMode" ? "matrixTheme" : equippedTheme);
      body.setAttribute("data-theme", attrValue);
      // Sakura is light: do NOT add .dark; others are dark themes
      if (LIGHT_THEMES.includes(equippedTheme)) {
        root.classList.remove("dark");
        body.classList.add("theme-light");
      } else {
        root.classList.add("dark");
        body.classList.remove("theme-light");
      }
      return;
    }

    // ── EQUIPPED THEME IS NULL: remove premium theme, hand control to standard Light/Dark ───
    root.removeAttribute("data-theme");
    root.removeAttribute("data-equipped-theme");
    body.removeAttribute("data-theme");

    // CRITICAL: Read preference from localStorage (source of truth) and systemTheme (reactive)
    const stored = getStandardThemePreference();
    const preference = (stored === "dark" || stored === "light" || stored === "system") ? stored : systemTheme;

    if (wantsDarkMode(preference)) {
      root.classList.add("dark");
      body.classList.remove("theme-light");
    } else {
      root.classList.remove("dark");
      body.classList.add("theme-light");
    }
  }, [equippedTheme, systemTheme]);

  return null;
}
